import { createContext, useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useLocalSettings } from './LocalSettings';
import { HEADERS_HEIGHT } from '../../utils/headers';

// Create and export the context
const AppState = createContext({});
export { AppState };

export const getType = (eventClass) => {
    if (!eventClass) return "unknown";
    
    const eventClassLower = eventClass.toLowerCase();
    
    // Handle RPO first as it's most specific
    if (eventClassLower.includes("rpo")) {
        return "rpo";
    }
    
    // Then handle other types
    if (eventClassLower.includes("maneuver") || eventClassLower.includes("manoeuvre") || eventClassLower.includes("period")) {
        return "maneuver";
    }
    if (eventClassLower.includes("photometry") || eventClassLower.includes("photometric")) {
        return "photometric";
    }
    if (eventClassLower.includes("launch")) {
        return "launch";
    }
    if (eventClassLower.includes("breakup")) {
        return "breakup";
    }
    
    return "unknown";
};

export const getCardColor = (type) => {
    if (!type) return 'secondary';
    
    const typeLower = type.toLowerCase();
    if (typeLower.includes('rpo')) return 'danger';
    if (typeLower.includes('maneuver') || typeLower.includes('manoeuvre')) return 'warning';
    if (typeLower.includes('photometry') || typeLower.includes('photometric')) return 'success';
    return 'secondary';
};

const getBaseUrl = (isCAC = false) => {
    // Get the current domain and determine the base URL
    const currentDomain = window.location.hostname;
    
    // Fallback to default logic
    if(currentDomain === 'localhost') return 'http://localhost:9001';
    else if (false === currentDomain.includes("sbms.")) // high side deployments use paths not subdomains
        return `https://${currentDomain}/nps`;
    else if(isCAC && currentDomain.includes("saberastro.com"))
        return `https://${currentDomain.replace('sbms.', 'nps.')}:444`; // CAC logins on saberastro.com only
    else
        return `https://${currentDomain.replace('sbms.', 'nps.')}`;
};

const getData = async (path, customHeaders = {}) => {
    try {
        const headers = {
            "Content-Type": "application/json",
            // Access token is automatically added from httpOnly cookie
            ...customHeaders
        };
    
        if (import.meta.env.VITE_API_KEY) {
            headers['api_key'] = import.meta.env.VITE_API_KEY;
        }

        const response = await fetch(`${getBaseUrl()}/${path}`, {
            method: 'GET',
            credentials: 'include',
            headers: headers,
        });

        if (!response.ok) {
            // For guest users, return empty data for certain endpoints
            if (response.status === 401 && path === 'apps') {
                return [];
            }
            throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('API Error:', error);
        let errorMessage = 'An error occurred while fetching data. ';
        
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
            errorMessage += 'Unable to connect to the server. Please check your internet connection and try again.';
        } else {
            errorMessage += error.message;
        }

        // Don't show error toast for guest users accessing apps endpoint
        if (!(error.message.includes('401') && path === 'apps')) {
            toast.error(errorMessage, {
                autoClose: 5000
            });
        }
        return null;
    }
};

const postLogin = async (username, password) => {
    if(!username || !password) return;
    return await fetch(`${getBaseUrl()}/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            "Content-Type": "application/json",
            "username": username,
            "password": password,
          },
    });
}

const postData = async (endpoint, data, method = "POST", customHeaders = {}, returnBadResponse=false) => {
    try {
        const response = await fetch(`${getBaseUrl()}/${endpoint}`, {
            method: method,
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            body: JSON.stringify(data),
        });

        if (!response.ok && response.status !== 200) {
            if (returnBadResponse) {
                return {response, isError: true}
            }
            throw new Error(`Failed to ${method.toLowerCase()} ${endpoint}: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        console.error('API Error:', error);
        let errorMessage = 'An error occurred while processing your request. ';
        
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
            errorMessage += 'Unable to connect to the server. Please check your internet connection and try again.';
        } else {
            errorMessage += error.message;
        }

        toast.error(errorMessage, {
            autoClose: 5000
        });
        return null;
    }
};

const fetchUserDetails = async (user, getData, setUserDetails) => {
    if (!user.loggedIn || user.isGuest) return;
    
    const details = await getData("user-details");
    setUserDetails(details);
};

const fetchOtherData = async (user, getData, setSatellites) => {
    if (!user.loggedIn) return;
    const satData = await getData("satellites");

    if (!satData || !Array.isArray(satData)) {
        console.error("Invalid satellite data received:", satData);
    }
    else {
        const formattedSats = satData.filter((s) => s).map(sat => {
            // If the data is already in the correct format, return it as is
            if (typeof sat === 'object' && sat.id && sat.name) {
                return sat;
            }
            // Otherwise, parse the string format
            const [id, name] = sat.split(" - ");
            return { id: id.trim(), name: name.trim() };
        });

        setSatellites(formattedSats);
    }
}

const patchData = async (endpoint, customHeaders = {}) => {
    try {
        const headers = {
            "Content-Type": "application/json",
            // Access token is automatically added from httpOnly cookie
            ...customHeaders
        };

        if (import.meta.env.VITE_API_KEY) {
            headers['api_key'] = import.meta.env.VITE_API_KEY;
        }

        const response = await fetch(`${getBaseUrl()}/${endpoint}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: headers
        });

        if (!response.ok) {
            throw new Error(`Failed to patch ${endpoint}: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        console.error('API Error:', error);
        let errorMessage = 'An error occurred while processing your request. ';
        
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
            errorMessage += 'Unable to connect to the server. Please check your internet connection and try again.';
        } else {
            errorMessage += error.message;
        }

        toast.error(errorMessage, {
            autoClose: 5000
        });
        throw error;
    }
};

const exerciseSingleton = {
    promise: null,
    data: null
};

const fetchExerciseData = async () => {
    if (exerciseSingleton.data) {
        return exerciseSingleton.data;
    }
    if (!exerciseSingleton.promise) {
        exerciseSingleton.promise = getData("get-exercise");
    }
    const response = await exerciseSingleton.promise;
    exerciseSingleton.data = response;
    return response;
};

export const AppProvider = ({ children }) => {

    const [user, setUser] = useState(() => {
        return {
            loggedIn: false,
            uuid: null,
            access_token: null,
            providers: [],
            isGuest: false,
            lastLoggedIn: -1
        };
    });

    const [scQueryParams, setSCQueryParams] = useState(null);
    
    const pendingRedirect = useRef(null);
    
    const isLocalDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
   
    const {
        profiles, setProfiles, selectedProfile, setSelectedProfile,
        lastDashboard, setLastDashboard
    } = useLocalSettings();

    const [userDetails, setUserDetails] = useState(null);
    const [userDetailsDirty, setUserDetailsDirty] = useState(false);

    const [satellites, setSatellites] = useState(null);
    const getSatName = useCallback((id) => {
        const lower = (id+"").toLowerCase();
        return satellites?.find((s) => (s.id+"").toLowerCase() === lower)?.name;
    }, [satellites]);

    useEffect(() => {
        fetchUserDetails(user, getData, setUserDetails);
        fetchOtherData(user, getData, setSatellites);
    }, [user]);

    useEffect(() => {
        if(userDetailsDirty) {
            fetchUserDetails(user, getData, setUserDetails);
            setUserDetailsDirty(false);
        }
    }, [userDetailsDirty, user]);

    const [exercise, setExercise] = useState(null);
    const fetchedExercise = useRef(false);

    useEffect(() => {
        const loadExercise = async () => {
            if (!fetchedExercise.current) {
                fetchedExercise.current = true;
                const data = await fetchExerciseData();
                setExercise(data);
            }
        };
        loadExercise();
    }, []);

    const includeExerciseData = useMemo(() => userDetails?.Settings?.includeExerciseData === "Yes", [userDetails]);

    const [simTime, setSimTime] = useState(null);
    const persistSatnos = useMemo(() => userDetails?.Settings?.persistentSatnos !== "No", [userDetails]);
    const lastSelectedSats = useRef(null);
    const [refreshLink, setRefreshLink] = useState(0);
    useEffect(() => {
    try {
        lastSelectedSats.current = persistSatnos ? JSON.parse(localStorage.getItem("UILastSatno")) : null;
        setRefreshLink((r) => r + 1);
    } catch (e) {
        // pass
    }
    }, [persistSatnos])
    const setLastSelectedSats = useCallback((newVal) => {
    if(persistSatnos) {
        lastSelectedSats.current = newVal;
        localStorage.setItem("UILastSatno", JSON.stringify(newVal));
    }
    }, [lastSelectedSats, persistSatnos]);

    const headersHeight = useMemo(() => {
        return HEADERS_HEIGHT(exercise, includeExerciseData);
    }, [exercise, includeExerciseData]);

    const store = useMemo(() => ({
        user,
        setUser,
        pendingRedirect,
        isLocalDev,
        getBaseUrl,
        getData,
        postLogin,
        postData,
        patchData,
        scQueryParams, setSCQueryParams,
        profiles, setProfiles, selectedProfile, setSelectedProfile,
        lastDashboard, setLastDashboard,
        userDetails, setUserDetails,
        userDetailsDirty, setUserDetailsDirty,
        satellites, getSatName,
        simTime, setSimTime,
        refreshLink, setRefreshLink, persistSatnos, lastSelectedSats, setLastSelectedSats,
        exercise,
        includeExerciseData,
        headersHeight
    }), [
        user, isLocalDev, pendingRedirect, 
        scQueryParams, setSCQueryParams, 
        profiles, setProfiles, selectedProfile, setSelectedProfile,
        lastDashboard, setLastDashboard,
        userDetails, setUserDetails,
        userDetailsDirty, setUserDetailsDirty,
        satellites, getSatName,
        simTime, setSimTime,
        refreshLink, setRefreshLink, persistSatnos, lastSelectedSats, setLastSelectedSats,
        exercise,
        includeExerciseData, 
        headersHeight
    ]);

    return (
        <AppState.Provider value={store}>      
            {children}
        </AppState.Provider>
    );
};